// Third-party libraries
import React, { useContext } from "react";
import { Link as DomLink } from "react-router-dom";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import AddShoppingCartSharpIcon from "@mui/icons-material/AddShoppingCartSharp";
import FavoriteIcon from "@mui/icons-material/Favorite";
import {
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Table,
  Stack,
  CardMedia,
  styled,
  Box,
} from "@mui/material";

// Local modules
import { GemstoneSearchContext } from "../../utils/stateHandlers/contexts";
import { Default, Mobile } from "../../utils/deviceHelpers";
import { storage } from "../../utils";
import placeholder from "../../../src/assets/images/DiaImageComingSoon.jpg";

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: "100%",
  width: "100%",
  transform: "scale(0.8)",
  [theme.breakpoints.down(800)]: {
    height: "350px",
  },
  [theme.breakpoints.between(800, 2000)]: {
    height: "300px",
  },
  [theme.breakpoints.between(2000, 2100)]: {
    height: "400px",
  },
  [theme.breakpoints.up(2100)]: {
    height: "400px",
  },
}));

export default function GemstoneTable(props) {
  const { gemstoneState, dispatch } = useContext(GemstoneSearchContext);
  const {
    currentTab,
    openPanel,
    tableHeaders,
    fancyColorHeaders,
    sidePanelTableHeaders,
    fcSidePanelHeaders,
    diamonds,
    recentlyViewed,
    selected,
    selectedDiamond,
    showFancyColorFilter,
  } = gemstoneState;
  const loggedIn = storage.getStorageItem("authToken");
  const rows =
    currentTab === "Results"
      ? diamonds
      : currentTab === "RecentlyViewed"
      ? recentlyViewed
      : selected;

  const formatCurrency = (number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(number);
  };

  function selectDiamond(diamondId, diamondChecked) {
    const diamondToSelect = diamonds.find((d) => {
      return diamondId === d.HooverStockId;
    });
    if (diamondChecked === false) {
      diamondToSelect.Checked = true;
      diamondToSelect.conditionalSale = false;
      dispatch({
        type: "setSelected",
        payload: [
          ...selected.filter(
            (d) => d.HooverStockId !== diamondToSelect.HooverStockId
          ),
          diamondToSelect,
        ],
      });
      dispatch({
        type: "setCartPreview",
        payload: [
          ...selected.filter(
            (d) => d.HooverStockId !== diamondToSelect.HooverStockId
          ),
          diamondToSelect,
        ],
      });
    } else {
      diamondToSelect.Checked = false;
      dispatch({
        type: "setSelected",
        payload: selected.filter((d) => d.HooverStockId !== diamondId),
      });
      dispatch({
        type: "setCartPreview",
        payload: selected.filter((d) => d.HooverStockId !== diamondId),
      });
    }
  }

  function addSingleDiamondToCartPreview(diamond) {
    diamond.conditionalSale = false;
    dispatch({
      type: "setCartPreviewSingle",
      payload: diamond,
    });
  }

  const GemstoneGrids = () => (
    <Grid container spacing={2} marginTop={"3rem"}>
      {rows.map((diamond) => (
        <Grid item xs={12} md={4} lg={3} xl={2}>
          <Card
            sx={{ width: "100%", minHeight: "100%", boxShadow: 4 }}
            elevation={12}
          >
            <StyledCardMedia
              image={diamond.ImageLink ? diamond.ImageLink : placeholder}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <IconButton
                  disabled={!loggedIn}
                  onClick={() =>
                    selectDiamond(diamond.HooverStockId, diamond.Checked)
                  }
                >
                  <FavoriteIcon
                    className="checkbox-for-diamond-row"
                    color={diamond.Checked ? "secondary" : "primary"}
                    sx={{
                      fill: diamond.Checked ? "red" : "none",
                      stroke: diamond.Checked ? "red" : "currentColor",
                      strokeWidth: 3,
                    }}
                  />
                </IconButton>

                <DomLink
                  to={{
                    pathname: `/gemstone/${diamond.HooverStockId}`,
                  }}
                  target="_blank"
                >
                  <Tooltip title="View Details" placement="top-start">
                    <IconButton
                      component="span"
                      key={diamond.HooverStockId + "view"}
                      id={diamond.HooverStockId}
                      className={
                        "pointer actions-icon " +
                        (selectedDiamond.HooverStockId === diamond.HooverStockId
                          ? " actions-icon--selected"
                          : "")
                      }
                      to={{
                        pathname: `/gemstone/${selectedDiamond.HooverStockId}`,
                      }}
                      target="_blank"
                    >
                      <ZoomInIcon
                        sx={{ verticalAlign: "middle", fontSize: "3rem" }}
                      />
                    </IconButton>
                  </Tooltip>
                </DomLink>
              </Box>
            </StyledCardMedia>
            <CardContent sx={{ minHeight: "120px" }}>
              <Stack gap={1}>
                <Typography
                  component={DomLink}
                  variant="h6"
                  to={{
                    pathname: `/gemstone/${diamond.HooverStockId}`,
                  }}
                  target="_blank"
                  sx={{ textDecoration: "underline" }}
                >
                  {diamond.Carat}ct {diamond.Color} {diamond.Shape}{" "}
                  {diamond.Type} {diamond.Gemstone},{" "}
                  {diamond.HooverCertification
                    ? `${diamond.HooverCertification}, `
                    : ""}{" "}
                  {`(${diamond.MinedLocation})`}
                </Typography>
                <Typography variant="h6">
                  {diamond.Measurement1.toFixed(2)} x{" "}
                  {diamond.Measurement2.toFixed(2)}{" "}
                  {diamond.Measurement3 !== 0
                    ? `x ${diamond.Measurement3.toFixed(2)}`
                    : ""}
                </Typography>
              </Stack>
            </CardContent>
            <CardActions
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Grid container>
                {props.loggedIn ? (
                  <React.Fragment>
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px",
                        }}
                      >
                        <Typography variant="subtitle2">
                          {formatCurrency(diamond.CalcPricePerCarat)} p/c
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px",
                        }}
                      >
                        <Typography variant="h3">
                          {formatCurrency(diamond.HooverPrice)}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          display: "flex",
                          alignItem: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Tooltip title="Add to Cart" placement="top-start">
                          <IconButton
                            component="span"
                            className={
                              "pointer actions-icon " +
                              (selectedDiamond.HooverStockId ===
                              diamond.HooverStockId
                                ? " actions-icon--selected"
                                : "")
                            }
                            onClick={(e) => {
                              dispatch({
                                type: "setOpenCartPreviewSingle",
                                payload: true,
                              });
                              addSingleDiamondToCartPreview(diamond);
                            }}
                          >
                            <AddShoppingCartSharpIcon
                              sx={{ verticalAlign: "middle", fontSize: "3rem" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                ) : null}
              </Grid>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  if (currentTab === "Results" && diamonds.length === 0) {
    return (
      <Typography variant="h6" sx={{ margin: "4rem" }}>
        No diamonds matched your search criteria.
      </Typography>
    );
  } else if (currentTab === "RecentlyViewed" && recentlyViewed.length === 0) {
    return (
      <Typography variant="h6" sx={{ margin: "4rem" }}>
        No recently viewed diamonds found.
      </Typography>
    );
  } else if (currentTab === "Selected" && selected.length === 0) {
    return (
      <Typography variant="h6" sx={{ margin: "4rem" }}>
        No diamonds are currently selected.
      </Typography>
    );
  }

  if (tableHeaders) {
    return (
      <>
        <Mobile>
          <Table size="small" className="results-table">
            {GemstoneGrids(
              !openPanel && !showFancyColorFilter
                ? tableHeaders
                : !openPanel && showFancyColorFilter
                ? fancyColorHeaders
                : openPanel && showFancyColorFilter
                ? fcSidePanelHeaders
                : sidePanelTableHeaders
            )}
          </Table>
        </Mobile>
        <Default>
          <Stack direction="row">
            <Table size="small" className="results-table">
              {GemstoneGrids(
                !openPanel && !showFancyColorFilter
                  ? tableHeaders
                  : !openPanel && showFancyColorFilter
                  ? fancyColorHeaders
                  : openPanel && showFancyColorFilter
                  ? fcSidePanelHeaders
                  : sidePanelTableHeaders
              )}
            </Table>
          </Stack>
        </Default>
      </>
    );
  }
}
